import React from 'react';
import DOMPurify from 'dompurify';
import { api } from '../api/client';

/**
 * MessageReference — Block 236 step 3.
 *
 * A REFERENCE to an email, not the email. That distinction is the whole design, and it
 * is stated on the element rather than left for the user to work out: the store keeps
 * who was emailed, the subject and the first 140 characters, for 180 days, and never the
 * body. An element that looks like an email client and then shows one sentence reads as
 * broken; one that says "from the email that created this" and shows a quoted line reads
 * as a citation, which is what it is.
 *
 * So: no envelope chrome, no reading pane, no reply box. A left rule, a label, a quoted
 * preview, and — where it can be said honestly — where this sits in its thread.
 *
 * It is EMBEDDED in the row it belongs to. It does not replace the proposal: the
 * proposal's own title, date and actions are the point of the row, and this is evidence
 * underneath them.
 *
 * Two requests, deliberately separated:
 *   /message-ref/:id  store only, no provider call, safe on every row of a list. This is
 *                     what renders by default.
 *   /message/:id      the provider, one call, only when the user asks to open it.
 */
// Fresh-eyes 17 (2026-10-12): this is now what "Show source" on a suggestion card opens. The card
// already read /message-ref (to know whether there is a web version to link to), so it passes
// that answer as `preloaded` and nothing is fetched twice. The provider's web link moved up onto
// the card as "Open the full email ↗", shown only when it exists; the in-app opener stays here
// as "Read it here", which works for every provider, IMAP included.
export default function MessageReference({ messageId, connectionId, fallbackSender, fallbackSubject, preloaded = null }) {
  const [ref, setRef] = React.useState(preloaded ? preloaded.reference : null);
  const [thread, setThread] = React.useState(preloaded ? preloaded.thread : null);
  const [known, setKnown] = React.useState(preloaded ? !!preloaded.known : null);      // null = still loading
  const [open, setOpen] = React.useState(null);        // the opened message, or a state object
  const [opening, setOpening] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    if (preloaded) {
      setRef(preloaded.reference); setThread(preloaded.thread); setKnown(!!preloaded.known);
      return undefined;
    }
    if (!messageId) { setKnown(false); return undefined; }
    const qs = connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : '';
    api(`/api/email/message-ref/${encodeURIComponent(messageId)}${qs}`)
      .then(r => r.json())
      .then(d => { if (!alive) return; setKnown(!!d.known); setRef(d.reference); setThread(d.thread); })
      .catch(() => { if (alive) setKnown(false); });
    return () => { alive = false; };
  }, [messageId, connectionId, preloaded]);

  async function openMessage() {
    setOpening(true);
    const qs = connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : '';
    try {
      const r = await api(`/api/email/message/${encodeURIComponent(messageId)}${qs}`);
      const d = await r.json();
      setOpen({ state: 'ok', message: d.message });
    } catch (err) {
      // The four non-200 states are real outcomes a user will meet, not errors to hide.
      // 410 in particular is the ordinary fate of a proposal from July.
      const status = err?.status;
      const body = err?.body || {};
      if (status === 410) setOpen({ state: 'gone', lastKnown: body.lastKnown || null });
      else if (status === 502) setOpen({ state: 'unavailable' });
      else if (status === 404) setOpen({ state: 'unresolved', reason: body.reason });
      else setOpen({ state: 'failed' });
    } finally {
      setOpening(false);
    }
  }

  // Nothing in the store and nothing passed in — render nothing rather than an empty box.
  if (known === false && !fallbackSender && !fallbackSubject) return null;

  const sender = ref?.fromName || ref?.fromEmail || fallbackSender || 'email';
  const subject = ref?.subject || fallbackSubject || null;

  return (
    <div style={S.wrap}>
      <div style={S.label}>
        <span aria-hidden="true">✉︎</span>
        <span>From the email that created this</span>
      </div>

      <div style={S.meta}>
        <strong style={{ color: 'var(--ink)' }}>{sender}</strong>
        {subject ? <>, “{subject}”</> : null}
        {ref?.date ? <span style={S.dim}> · {fmtDate(ref.date)}</span> : null}
      </div>

      {ref?.preview ? (
        <div style={S.preview}>“{ref.preview}”</div>
      ) : known === false ? (
        <div style={S.dim}>Aidey did not record this message, so only what the proposal captured is shown.</div>
      ) : null}

      {/* The honest limit, stated once, where the truncation actually happens. */}
      {ref?.preview ? (
        <div style={S.limit}>
          Aidey keeps the first 140 characters, not the message itself.
        </div>
      ) : null}

      <ThreadSummary thread={thread} />

      <div style={S.actions}>
        {!open && (
          <button type="button" onClick={openMessage} disabled={opening || !messageId} style={S.link}>
            {opening ? 'Opening…' : 'Read it here'}
          </button>
        )}
        {open && <OpenedState open={open} onRetry={openMessage} />}
      </div>
    </div>
  );
}

// ── The thread summary ────────────────────────────────────────────────────────
//
// The one thing here the inbox cannot show at all, and it costs no provider call.
// Every branch below is about saying only what the data supports.
function ThreadSummary({ thread }) {
  if (!thread) return null;

  // No thread key at all. Block 210's derivation landed late, so most historical rows
  // are a thread of one by OMISSION, not by fact. "1 message" would be a claim; this
  // says nothing instead.
  if (thread.threadKeyMissing) {
    return <div style={S.thread}>Aidey cannot group this message into a thread.</div>;
  }

  const { count, outboundCount, inboundCount, lastOutboundAt, lastInboundAt, awaitingReply, coverageFrom } = thread;

  // A thread of one. Which side it is on changes what can be said, and neither version
  // pretends to know what happened next.
  if (count <= 1) {
    if (outboundCount === 1) {
      return (
        <div style={S.thread}>
          One message, sent {fmtDate(lastOutboundAt)}.{' '}
          {awaitingReply === true
            ? <strong>No reply since.</strong>
            : <span style={S.dim}>Aidey has no record of a reply, but replies are only tracked from {fmtDate(coverageFrom)}{coverageFrom ? '' : ' once your inbox has been opened'}.</span>}
        </div>
      );
    }
    return <div style={S.thread}>One message, received {fmtDate(lastInboundAt)}. Nothing else recorded in this thread.</div>;
  }

  const parts = [`${count} messages`];
  if (outboundCount && inboundCount) parts.push(`${outboundCount} from you`);
  return (
    <div style={S.thread}>
      {parts.join(' · ')}
      {lastOutboundAt ? <> · you replied {fmtDate(lastOutboundAt)}</> : null}
      {awaitingReply === true ? <> · <strong>no reply since</strong></> : null}
      {awaitingReply === false && lastInboundAt ? <> · last reply {fmtDate(lastInboundAt)}</> : null}
      {awaitingReply === null ? <span style={S.dim}> · replies only tracked from {fmtDate(coverageFrom)}</span> : null}
    </div>
  );
}


// ── The five states of opening ────────────────────────────────────────────────
function OpenedState({ open, onRetry }) {
  if (open.state === 'ok') {
    const m = open.message;
    return (
      <div style={S.opened}>
        <div style={S.openedHead}>{m.subject}</div>
        <div style={S.dim}>{m.from} · {fmtDate(m.date)}</div>
        {/* m.body is RAW PROVIDER HTML: an email in the owner's mailbox, straight
            from extractGmailBody (routes/email/inbox.js:1839) and returned with no
            server-side sanitisation. Anyone who can email this user controls it --
            no account, no portal link, no relationship with the business.
            Unsanitised, it ran their script in the owner's authenticated session.
            Every other dangerouslySetInnerHTML in this app that renders
            externally-influenced HTML already does exactly this. */}
        <div
          style={S.body}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body || '') }}
        />
      </div>
    );
  }
  if (open.state === 'gone') {
    // The ordinary fate of a July proposal. Not an error — a fact about the mailbox.
    return (
      <div style={S.note}>
        <strong>This email is no longer in the mailbox.</strong>{' '}
        {open.lastKnown
          ? 'It was deleted after Aidey saw it, so only the summary above remains.'
          : 'It was deleted, and Aidey has no record of it beyond what the proposal captured.'}
      </div>
    );
  }
  if (open.state === 'unavailable') {
    return (
      <div style={S.note}>
        Aidey could not reach your mail provider, so it cannot tell whether this email is
        still there.{' '}
        <button type="button" onClick={onRetry} style={S.link}>Try again</button>
      </div>
    );
  }
  if (open.state === 'unresolved') {
    return (
      <div style={S.note}>
        Aidey cannot tell which mailbox this email came from, so it cannot open it. This
        happens with items detected before Aidey started recording the mailbox.
      </div>
    );
  }
  return (
    <div style={S.note}>
      Something went wrong opening this email.{' '}
      <button type="button" onClick={onRetry} style={S.link}>Try again</button>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return 'an unknown date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'an unknown date';
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

const S = {
  wrap: { marginTop: 10, paddingLeft: 10, borderLeft: '3px solid var(--line-soft)', fontSize: 12, color: 'var(--ink-3)' },
  label: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3)', fontWeight: 700, marginBottom: 3 },
  meta: { color: 'var(--ink-2)', lineHeight: 1.45 },
  preview: { marginTop: 3, fontStyle: 'italic', color: 'var(--ink-3)', lineHeight: 1.5 },
  limit: { marginTop: 2, fontSize: 12, color: 'var(--ink-3)' },
  thread: { marginTop: 6, color: 'var(--ink-2)' },
  dim: { color: 'var(--ink-3)' },
  actions: { marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  link: { background: 'none', border: 'none', padding: 0, color: 'var(--ink)', fontWeight: 600, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' },
  note: { marginTop: 4, background: 'var(--surface-2)', border: '1px solid var(--line-soft)', borderRadius: 6, padding: '8px 10px', color: 'var(--ink-2)', lineHeight: 1.5 },
  opened: { marginTop: 6, background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 6, padding: '10px 12px' },
  openedHead: { fontWeight: 700, color: 'var(--ink)', fontSize: 13, marginBottom: 2 },
  body: { marginTop: 8, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6, maxHeight: 320, overflowY: 'auto' },
};
